import React from 'react'
import { Button } from '@/components/ui/button'
import SbEditable from 'storyblok-react'
import RichText from '@/utils/RichText'

import Cloud from './asset/cloud'
import CloudLarge from './asset/cloud-large'

export interface Props {
  blok: StoryblokComponent & {
    label: string
    heading: string
    sub_heading: string
    description: string
    button_cta_link: string
    button_cta_text: string
    avatar: string
    avatar_name: string
    avatar_position: string
    marginTop?: number
    marginBottom?: number
  }
}

export const StatementWithOwnerOfStatement = ({ blok }: Props) => {
  return (
    /** @ts-ignore **/
    <SbEditable content={blok} key={blok._uid}>
      <div className="relative font-display w-full max-w-6xl p-4 md:py-12 mb-10 md:mb-14 lg:h-screen lg:mb-[20%] 2xl:mb-[-20px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="block lg:hidden">
            <RightCloud blok={blok} />
          </div>
          <div className="absolute -left-[15rem] md:-left-10 lg:-left-[45%] 2xl:-left-[45%] md:top-[10%] md:translate-y-[-10%] lg:top-1/2 lg:-translate-y-72 lg: 2xl:top-[1%] 2xl:-translate-y-[10%] opacity-20">
            <LeftCloud />
          </div>
          <div className="col-span-3 flex flex-col gap-4 max-w-5xl z-10 bg-none mt-80">
            <div className="prose uppercase text-brightgreen font-medium mb-4 text-xs lg:text-medium">
              <RichText>{blok.label}</RichText>
            </div>
            {/* Header | RTE */}
            <h1 className="prose text-2xl lg:text-4xl leading-normal tracking-wide font-semibold max-w-3xl">
              <RichText>{blok.heading}</RichText>
            </h1>

            <h5 className="prose md:text-lg lg:text-xl font-medium">
              <RichText>{blok.sub_heading}</RichText>
            </h5>

            {/* Body */}
            <div className="prose tracking-tight leading-normal text-xs lg:text-sm">
              <RichText>{blok.description}</RichText>
            </div>

            {/* Button */}
            <div>
              <Button
                className="shadow-lg"
                variant="secondary_green"
                to={blok.button_cta_link?.cached_url || '#'}
              >
                {blok.button_cta_text}
              </Button>
            </div>
          </div>
          <div className="col-span-1">
            <div className="relative">
              <div className="relative hidden lg:block right-[40rem]">
                <RightCloud blok={blok} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </SbEditable>
  )
}

const LeftCloud = () => {
  return (
    <div>
      <CloudLarge
        className="w-[860px] h-[800px] md:w-[928px] md:h-[1000px] lg:w-[1300px] lg:h-[1024px] 2xl:w-[2000px] 2xl:h-[1400px]"
        width={1300}
        height={1024}
      />
    </div>
  )
}

const RightCloud = ({ blok }: Props) => {
  return (
    <div>
      <div className="absolute top-[12%] right-[-30%] md:top-[1rem] md:translate-y-[5%] md:left-[23rem] lg:top-[30%] lg:right-[50rem] lg:translate-y-[15%] lg:translate-x-[15%]">
        <Cloud
          className="opacity-50 z-[-999] w-[400px] h-[300px] md:w-[400px] md:h-[400px] lg:w-[700px] lg:h-[700px]"
          width={700}
          height={700}
        />
      </div>
      <div className="absolute top-[15%] right-[7%] md:top-[7rem] md:translate-y-[10%] md:left-[32rem] lg:hidden">
        <Avatar blok={blok} />
      </div>
      <div className="absolute hidden lg:block top-[20rem] translate-y-[90%] left-[53rem]">
        <Avatar blok={blok} />
      </div>
      <div className="absolute top-[30%] right-[7%] md:top-[15rem] md:translate-y-[90%] md:right-[3rem] lg:top-[38rem] lg:translate-y-[90%] lg:right-[-44rem]">
        <AvatarPosition blok={blok} />
      </div>
    </div>
  )
}

const Avatar = ({ blok }: Props) => {
  return (
    <div className="relative z-[9998]">
      <div className="w-[6rem] h-[6rem] md:w-[8rem] md:h-[8rem] lg:w-40 lg:h-40 rounded-full overflow-hidden border-4 border-brightgreen shadow-lg">
        <img
          src={blok.avatar}
          alt={blok.avatar_name}
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  )
}

const AvatarPosition = ({ blok }: Props) => {
  return (
    <div className="text-right">
      <div className="text-xs lg:text-sm font-medium"><RichText>{blok.avatar_name}</RichText></div>
      <div className="text-xs lg:text-sm font-medium uppercase text-brightgreen">
        <RichText>{blok.avatar_position}</RichText>
      </div>
    </div>
  )
}
StatementWithOwnerOfStatement.displayName = 'StatementWithOwnerOfStatement'